import {
  ShieldCheck,
  Banknote,
  Users,
  Settings,
  LayoutGrid,
  ClipboardCheck,
  SlidersHorizontal,
  TrendingUp,
  Wallet,
  CircleDollarSign,
  UserCog,
  Building2,
  FileText,
  HardHat,
  ClipboardList,
  Wrench,
  ToggleLeft,
  Shield,
  Briefcase,
  Network,
  IdCard,
  GitMerge,
  Coins,
  Globe,
  UserCheck,
  ArrowLeftRight,
  Rss,
  Palette,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type AdminGroup = 'oversight' | 'payroll' | 'employment' | 'resources' | 'system';

export type AdminTab =
  | 'dashboard'
  | 'approvals'
  | 'approval-config'
  | 'activity'
  | 'payroll'
  | 'allowance-types'
  | 'users'
  | 'hospitals'
  | 'treatments'
  | 'helmets'
  | 'report-types'
  | 'employment-roles'
  | 'hospital-assignments'
  | 'report-builder'
  | 'relations'
  | 'settings'
  | 'features'
  | 'roles'
  | 'currencies'
  | 'region-config'
  | 'user-approvals'
  | 'user-impersonation'
  | 'rss-feeds'
  | 'hospital-reports'
  | 'colors'
  | 'widget-layouts';

export interface AdminNavItem {
  tabId: AdminTab;
  path: string;
  labelKey: string;
  icon: LucideIcon;
  group: AdminGroup;
}

export interface AdminGroupConfig {
  id: AdminGroup;
  labelKey: string;
  icon: LucideIcon;
}

export const ADMIN_GROUPS: AdminGroupConfig[] = [
  { id: 'oversight', labelKey: 'adminNav.oversight', icon: ShieldCheck },
  { id: 'payroll', labelKey: 'adminNav.payroll', icon: Banknote },
  { id: 'employment', labelKey: 'adminNav.employment', icon: IdCard },
  { id: 'resources', labelKey: 'adminNav.resources', icon: Users },
  { id: 'system', labelKey: 'adminNav.system', icon: Settings },
];

export const ADMIN_NAV_ITEMS: AdminNavItem[] = [
  // Oversight
  { tabId: 'dashboard', path: '/admin', labelKey: 'adminNav.dashboard', icon: LayoutGrid, group: 'oversight' },
  { tabId: 'hospital-reports', path: '/admin/hospital-reports', labelKey: 'adminNav.hospitalReports', icon: Building2, group: 'oversight' },
  { tabId: 'activity', path: '/admin/activity', labelKey: 'adminNav.activityLog', icon: TrendingUp, group: 'oversight' },
  // Payroll
  { tabId: 'approvals', path: '/admin/approvals', labelKey: 'adminNav.reportApprovals', icon: ClipboardCheck, group: 'payroll' },
  { tabId: 'payroll', path: '/admin/payroll', labelKey: 'adminNav.payrollManagement', icon: Wallet, group: 'payroll' },
  // Employment
  { tabId: 'employment-roles', path: '/admin/employment-roles', labelKey: 'adminNav.employmentRoles', icon: Briefcase, group: 'employment' },
  { tabId: 'hospital-assignments', path: '/admin/hospital-assignments', labelKey: 'adminNav.hospitalAssignments', icon: GitMerge, group: 'employment' },
  { tabId: 'allowance-types', path: '/admin/allowance-types', labelKey: 'adminNav.allowanceTypes', icon: CircleDollarSign, group: 'employment' },
  // Resources
  { tabId: 'user-approvals', path: '/admin/user-approvals', labelKey: 'adminNav.userApprovals', icon: UserCheck, group: 'resources' },
  { tabId: 'users', path: '/admin/users', labelKey: 'adminNav.usersManagement', icon: UserCog, group: 'resources' },
  { tabId: 'roles', path: '/admin/roles', labelKey: 'adminNav.roles', icon: Shield, group: 'resources' },
  { tabId: 'user-impersonation', path: '/admin/user-impersonation', labelKey: 'adminNav.userImpersonation', icon: ArrowLeftRight, group: 'resources' },
  { tabId: 'hospitals', path: '/admin/hospitals', labelKey: 'adminNav.hospitals', icon: Building2, group: 'employment' },
  { tabId: 'treatments', path: '/admin/treatments', labelKey: 'adminNav.treatments', icon: FileText, group: 'employment' },
  { tabId: 'helmets', path: '/admin/helmets', labelKey: 'adminNav.helmets', icon: HardHat, group: 'employment' },
  { tabId: 'report-types', path: '/admin/report-types', labelKey: 'adminNav.reportTypes', icon: ClipboardList, group: 'employment' },
  { tabId: 'report-builder', path: '/admin/report-builder', labelKey: 'adminNav.reportBuilder', icon: SlidersHorizontal, group: 'employment' },
  // System
  { tabId: 'region-config', path: '/admin/region-config', labelKey: 'adminNav.regionalSettings', icon: Globe, group: 'system' },
  { tabId: 'settings', path: '/admin/settings', labelKey: 'adminNav.systemSettings', icon: Wrench, group: 'system' },
  { tabId: 'colors', path: '/admin/colors', labelKey: 'adminNav.colors', icon: Palette, group: 'system' },
  { tabId: 'widget-layouts', path: '/admin/widget-layouts', labelKey: 'adminNav.widgetLayouts', icon: LayoutGrid, group: 'system' },
  { tabId: 'rss-feeds', path: '/admin/rss-feeds', labelKey: 'adminNav.rssFeeds', icon: Rss, group: 'system' },
  { tabId: 'relations', path: '/admin/relations', labelKey: 'adminNav.relations', icon: Network, group: 'system' },
];

/** Permission-based access for admin tabs using DB permissions (tab:*) */
function tabToPermission(tab: AdminTab): string {
  return `tab:${tab.replace(/-/g, '_')}`;
}

const ALWAYS_VISIBLE_TABS: Set<AdminTab> = new Set([]);

export function canAccessAdminTab(tab: AdminTab, permissions: string[]): boolean {
  if (ALWAYS_VISIBLE_TABS.has(tab)) return true;
  return permissions.includes(tabToPermission(tab));
}

/** Map a route path to its group */
export function getGroupFromPath(pathname: string): AdminGroup | null {
  // Dashboard is the index route for admin
  if (pathname === '/admin') return 'oversight';
  const item = ADMIN_NAV_ITEMS.find(
    i => i.path !== '/admin' && (pathname === i.path || pathname.startsWith(i.path + '/')),
  );
  return item?.group ?? null;
}
